import * as React from "react"

type Event = MouseEvent | TouchEvent | KeyboardEvent

/**
 * Custom hook to detect clicks outside a specified element.
 *
 * @param ref - React ref object pointing to the element to detect outside clicks for.
 * @param handler - Function to call when an outside click is detected.
 */
export const useClickOutside = <T extends HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: Event) => void,
) => {
  const savedHandler = React.useRef(handler)

  React.useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  React.useEffect(() => {
    const listener = (event: Event) => {
      const element = ref.current

      // Check if the event is a TouchEvent or MouseEvent
      if ((window.TouchEvent && event instanceof TouchEvent) || event instanceof MouseEvent) {
        // If the element is not defined or the event target is within the element, do nothing
        if (!element || element.contains(event.target as Node)) {
          return
        }

        // If the event is a MouseEvent and occurs outside the viewport, do nothing
        if (
          event instanceof MouseEvent &&
          (event.clientX >= document.documentElement.offsetWidth ||
            event.clientY >= document.documentElement.offsetHeight)
        ) {
          return
        }
      } else if (event instanceof KeyboardEvent) {
        // If the event is a KeyboardEvent and the key is not Escape or Tab, do nothing
        if (event.key !== "Escape" && event.key !== "Tab") {
          return
        }
      }

      // Call the handler if none of the above conditions are met
      savedHandler.current(event)
    }

    document.addEventListener("mousedown", listener)
    document.addEventListener("touchstart", listener)
    document.addEventListener("keydown", listener)

    return () => {
      document.removeEventListener("mousedown", listener)
      document.removeEventListener("touchstart", listener)
      document.removeEventListener("keydown", listener)
    }
  }, [ref])
}
